/**
 * Own fields (ADR-0102 addendum): simple fields the author defines in the
 * action log (`defineField` / `updateField` / `removeField`) and references
 * from adapted text as live value nodes (`sourceKey: 'custom_…'`).
 *
 * Definitions are log entries — replayable, undoable, reviewable, carried
 * into variants. VALUES are the host's: they live in its form values under
 * the reserved `custom_` prefix and are validated with
 * `validateCustomValues`. No conditions, no calculations.
 *
 * Removal policy: a field still referenced by effective content cannot be
 * removed (`field_in_use`) — neither by `removeField` nor by undoing its
 * `defineField`. Remove (or undo) the text that uses it first. A replay
 * that meets a dangling reference anyway prints it as literal text and
 * reports it (`DetachedValue.reason: 'field_removed'`).
 */
import { createCompileContext } from '../block/context.js';
import { foldLog, isContentEntry, isFieldEntry } from './fold.js';
import { CUSTOM_FIELD_PREFIX, CUSTOM_FIELDS_GROUP_ID, isCustomFieldKey } from './types.js';
export const CUSTOM_FIELD_TYPES = ['text', 'date', 'number', 'money', 'boolean'];
/** `custom_` + 1–60 of `[a-z0-9_]`. */
export const CUSTOM_FIELD_KEY_PATTERN = /^custom_[a-z0-9_]{1,60}$/;
const MAX_LABEL = 200;
const MAX_HELP = 1000;
export const isValidCustomFieldKey = (key) => typeof key === 'string' && CUSTOM_FIELD_KEY_PATTERN.test(key);
const validLabel = (label) => typeof label === 'string' && label.trim() !== '' && label.length <= MAX_LABEL;
const validType = (type) => CUSTOM_FIELD_TYPES.includes(type);
const validHelp = (help) => help == null || (typeof help === 'string' && help.length <= MAX_HELP);
/** What is wrong with a field entry's payload on its own (no log context). */
function payloadProblem(e) {
    const p = (e.payload ?? {});
    if (!isValidCustomFieldKey(p.key))
        return 'invalid_field';
    if (e.op === 'defineField')
        return validLabel(p.label) && validType(p.type) && validHelp(p.help) ? null : 'invalid_field';
    if (e.op === 'updateField') {
        if (p.label !== undefined && !validLabel(p.label))
            return 'invalid_field';
        if (p.type !== undefined && !validType(p.type))
            return 'invalid_field';
        return validHelp(p.help) ? null : 'invalid_field';
    }
    return null;
}
/** Folds the own-field entries of a log (undo applied) into the fields in effect. */
export function foldCustomFields(log, folded = foldLog(log)) {
    const fields = new Map();
    const claimedKeys = new Set();
    const applied = new Set();
    const invalid = new Map();
    const keyOf = new Map();
    for (const e of [...log.entries].sort((a, b) => a.seq - b.seq)) {
        if (!isFieldEntry(e) || folded.malformed.has(e.seq))
            continue;
        const key = e.payload?.key;
        if (typeof key === 'string')
            keyOf.set(e.seq, key);
        if (folded.undone.has(e.seq))
            continue;
        const problem = payloadProblem(e);
        if (problem) {
            invalid.set(e.seq, problem);
            continue;
        }
        const k = key;
        if (e.op === 'defineField') {
            if (claimedKeys.has(k)) {
                invalid.set(e.seq, 'duplicate_field');
                continue;
            }
            claimedKeys.add(k);
            const { label, type, help } = e.payload;
            fields.set(k, { key: k, label: label.trim(), type, ...(help ? { help } : {}), definedBy: e.seq, updatedBy: [], lastSeq: e.seq });
            applied.add(e.seq);
            continue;
        }
        const current = fields.get(k);
        if (!current) {
            invalid.set(e.seq, 'unknown_field');
            continue;
        }
        if (e.op === 'updateField') {
            const { label, type, help } = e.payload;
            const next = { ...current, updatedBy: [...current.updatedBy, e.seq], lastSeq: e.seq };
            if (label !== undefined)
                next.label = label.trim();
            if (type !== undefined)
                next.type = type;
            if (help === null || help === '')
                delete next.help;
            else if (help !== undefined)
                next.help = help;
            fields.set(k, next);
        }
        else {
            fields.delete(k);
        }
        applied.add(e.seq);
    }
    return { fields, claimedKeys, applied, invalid, keyOf };
}
/** The own fields in effect after the log, in definition order. */
export function deriveCustomFieldDefinitions(log) {
    return [...foldCustomFields(log).fields.values()];
}
/** Own field type → the form's slot type. */
export const customFieldSlotType = (type) => (type === 'money' ? 'currency' : type);
/** Own field type → the value node format. */
export const customFieldValueFormat = (type) => type === 'money' ? 'currency' : type === 'boolean' ? 'text' : type;
/**
 * The own fields as form fields — the same shape as
 * `describeCompositionForm(...).fields`, owned by the "Egna uppgifter"
 * group (`CUSTOM_FIELDS_GROUP_ID`). Never required.
 */
export function deriveCustomFields(log) {
    return deriveCustomFieldDefinitions(log).map((f) => customFieldToFormField(f));
}
export const customFieldToFormField = (f) => ({
    key: f.key,
    label: f.label,
    type: customFieldSlotType(f.type),
    required: false,
    statutory: false,
    ...(f.help ? { help: f.help } : {}),
    groupId: CUSTOM_FIELDS_GROUP_ID,
    occurrences: [],
});
/* ------------------------------------------------------------------ */
/* Keys                                                                */
/* ------------------------------------------------------------------ */
/** ASCII slug of a label: "Datum för besiktning" → "datum_for_besiktning". */
export function slugifyFieldLabel(label) {
    const slug = label
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 40)
        .replace(/_+$/g, '');
    return slug === '' ? 'falt' : slug;
}
/** A fresh `custom_<slug>` key for a label, unique against `taken` (`_2`, `_3`, …). */
export function customFieldKey(label, taken) {
    const used = new Set(taken);
    const base = `${CUSTOM_FIELD_PREFIX}${slugifyFieldLabel(label)}`;
    if (!used.has(base))
        return base;
    for (let i = 2;; i++) {
        const candidate = `${base}_${i}`;
        if (!used.has(candidate))
            return candidate;
    }
}
/* ------------------------------------------------------------------ */
/* References                                                          */
/* ------------------------------------------------------------------ */
/** Field keys referenced (value nodes with a `sourceKey`) in an inline run, in order, with repeats. */
export function referencedKeys(content, out = []) {
    for (const n of content ?? []) {
        if (n.type === 'value' && n.sourceKey != null)
            out.push(n.sourceKey);
        else if (n.type === 'strong' || n.type === 'emphasis' || n.type === 'label')
            referencedKeys(n.content, out);
    }
    return out;
}
/** Field keys a content entry's payload references. */
export function entryReferences(entry) {
    const payload = entry.payload;
    if (!payload)
        return [];
    switch (entry.op) {
        case 'replaceText':
        case 'insertParagraph':
            return Array.isArray(payload.content) ? referencedKeys(payload.content) : [];
        case 'insertClause':
            return Array.isArray(payload.paragraphs) ? payload.paragraphs.flatMap((p) => (Array.isArray(p) ? referencedKeys(p) : [])) : [];
        case 'replaceRow':
        case 'insertRow':
            return [
                ...(Array.isArray(payload.term) ? referencedKeys(payload.term) : []),
                ...(Array.isArray(payload.description)
                    ? payload.description.flatMap((p) => (Array.isArray(p) ? referencedKeys(p) : []))
                    : []),
            ];
        default:
            return [];
    }
}
/** Own field key → the effective (not undone) content entries referencing it. */
export function customFieldUsage(log, folded = foldLog(log)) {
    const usage = new Map();
    for (const e of [...log.entries].sort((a, b) => a.seq - b.seq)) {
        if (!isContentEntry(e) || folded.malformed.has(e.seq) || folded.undone.has(e.seq))
            continue;
        if (folded.suggestions.get(e.seq)?.state === 'rejected')
            continue;
        for (const key of new Set(entryReferences(e))) {
            if (!isCustomFieldKey(key))
                continue;
            usage.set(key, [...(usage.get(key) ?? []), e.seq]);
        }
    }
    return usage;
}
/**
 * Why a new field entry (or a content entry's own-field references) cannot
 * be appended to `log`, or `null`:
 *
 * - `invalid_field` — malformed key (`custom_[a-z0-9_]+`), label or type;
 * - `duplicate_field` — `defineField` of a key the log already claimed;
 * - `unknown_field` — update / remove of a field not in effect, or content
 *   referencing a `custom_` key that is not defined;
 * - `field_in_use` — `removeField` while effective content references it.
 *
 * Other entries → `null` (see `undoRefusal` for undo).
 */
export function fieldEntryRefusal(log, entry) {
    if (isContentEntry(entry)) {
        const keys = entryReferences(entry).filter(isCustomFieldKey);
        if (keys.length === 0)
            return null;
        const { fields } = foldCustomFields(log);
        return keys.every((k) => fields.has(k)) ? null : 'unknown_field';
    }
    if (!isFieldEntry(entry))
        return null;
    const field = entry;
    const problem = payloadProblem(field);
    if (problem)
        return problem;
    const { fields, claimedKeys } = foldCustomFields(log);
    const key = field.payload.key;
    if (field.op === 'defineField')
        return claimedKeys.has(key) ? 'duplicate_field' : null;
    if (!fields.has(key))
        return 'unknown_field';
    if (field.op === 'removeField' && (customFieldUsage(log).get(key)?.length ?? 0) > 0)
        return 'field_in_use';
    return null;
}
/* ------------------------------------------------------------------ */
/* Values                                                              */
/* ------------------------------------------------------------------ */
const DEFAULT_FORMAT = createCompileContext({ now: '1970-01-01T00:00:00.000Z' }).format;
const toNumber = (raw) => {
    if (typeof raw === 'number')
        return Number.isFinite(raw) ? raw : null;
    if (typeof raw === 'string' && raw.trim() !== '') {
        const n = Number(raw.replace(/\s/g, '').replace(',', '.'));
        return Number.isFinite(n) ? n : null;
    }
    return null;
};
const decimals = (n) => {
    if (Number.isInteger(n))
        return 0;
    const frac = String(n).split('.')[1] ?? '';
    return Math.min(3, frac.length);
};
const toBoolean = (raw) => {
    if (typeof raw === 'boolean')
        return raw;
    if (raw === 'true' || raw === 'ja' || raw === 'Ja')
        return true;
    if (raw === 'false' || raw === 'nej' || raw === 'Nej')
        return false;
    return null;
};
const ISO_DATE = /^\d{4}-\d{2}-\d{2}/;
/** Plain text of a free-text value: inner whitespace collapsed (line breaks kept), trimmed; empty ⇒ `null`. */
export function textOfValue(raw) {
    if (raw === null || raw === undefined)
        return null;
    if (typeof raw === 'object')
        return null;
    const s = String(raw)
        .replace(/[^\S\n]+/g, ' ')
        .replace(/[ \t]*\n[ \t]*/g, '\n')
        .trim();
    return s === '' ? null : s;
}
/**
 * Prints an own-field value the way the document shows it: dates as
 * `YYYY-MM-DD`, numbers with the locale's grouping, amounts with " kr",
 * yes/no as "Ja"/"Nej". `null` = blank (the fill-in rule).
 */
export function formatCustomValue(type, raw, format = DEFAULT_FORMAT) {
    switch (type) {
        case 'date': {
            const s = textOfValue(raw);
            if (s == null)
                return { text: null, raw: null };
            return ISO_DATE.test(s) ? { text: format.date(s), raw: s.slice(0, 10) } : { text: s, raw: s };
        }
        case 'number': {
            const n = toNumber(raw);
            return n == null ? { text: textOfValue(raw), raw: null } : { text: format.number(n, decimals(n)), raw: n };
        }
        case 'money': {
            const n = toNumber(raw);
            if (n == null)
                return { text: textOfValue(raw), raw: null };
            return { text: Number.isInteger(n) ? format.currency(n) : `${format.number(n, 2)} kr`, raw: n };
        }
        case 'boolean': {
            const b = toBoolean(raw);
            return b == null ? { text: null, raw: null } : { text: b ? 'Ja' : 'Nej', raw: b };
        }
        default: {
            const s = textOfValue(raw);
            return { text: s, raw: s };
        }
    }
}
/**
 * Checks own-field values (`custom_…` keys of `values`) against the log's
 * definitions: type mismatches are errors (`custom_value_type`), values for
 * keys that are not defined are warnings (`custom_value_unknown` — e.g. the
 * field was removed; the host may drop them). Empty values are fine.
 * Messages carry the key, never the value.
 */
export function validateCustomValues(log, values) {
    const { fields } = foldCustomFields(log);
    const out = [];
    for (const [key, raw] of Object.entries(values)) {
        if (!isCustomFieldKey(key))
            continue;
        const field = fields.get(key);
        if (!field) {
            out.push({ severity: 'warning', code: 'custom_value_unknown', message: `No own field "${key}" is defined.`, slotKey: key });
            continue;
        }
        if (raw === null || raw === undefined || raw === '')
            continue;
        let ok;
        switch (field.type) {
            case 'text':
                ok = typeof raw === 'string' || typeof raw === 'number';
                break;
            case 'date':
                ok = typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw) && !Number.isNaN(Date.parse(raw));
                break;
            case 'number':
            case 'money':
                ok = toNumber(raw) != null;
                break;
            case 'boolean':
                ok = typeof raw === 'boolean';
                break;
        }
        if (!ok) {
            out.push({
                severity: 'error',
                code: 'custom_value_type',
                message: `Own field "${key}" expects ${field.type === 'money' ? 'an amount' : `a ${field.type}`}.`,
                slotKey: key,
            });
        }
    }
    return out;
}
//# sourceMappingURL=fields.js.map